import Component from '../abstracts/Component';
import TrackingComponent from './TrackingComponent';
import WishlistLogic from '../logics/WishlistLogic';
import vex from 'vex-js';
import OnDemandLiveRegion from "../utils/OnDemandLiveRegion";
import { trapFocusOnModal, removeTrapFocusOnModal } from '../utils/TrapFocus';
import { removeAlgoliaAttributes } from '../utils/algoliaTrackings';

export default class PdpMainComponent extends Component {

  get SELECTORS() {
    return {
      wishlistButton: "[data-js-wishlist]",
      infoSection: "[data-js-info]",
      sizeGuideTrigger: "[data-js-size-guide]",
      swatchBullet: "[data-js-swatch]",
      sizeBullet: "[data-size]",
      swatchColor: "[data-js-swatch-color]",
      // pdpCta/chatCta interactions
      pdpCtaMobile: "[data-pdp-cta].--mobile",
      pdpNotifyMeMobile: "[data-pdp-notifyme].--mobile",
      chatCta: '.embeddedServiceHelpButton.livechat-button .helpButton',
    };
  }

  get CLASSES() {
    return {
      iconWishlistEmpty: "icon-wishlist-empty",
      iconWishlistFull: "icon-wishlist-full",
    };
  }

  constructor(compEl) {
    super(compEl);
    this.trackingComponent = new TrackingComponent();
    this.wishlistLogic = new WishlistLogic();
    this.liveRegion = new OnDemandLiveRegion({
      level: "assertive",
      delay: 1000,
    });
  }

  readDOM() {
    return {
      wishlistButton: this.$component.querySelector(this.SELECTORS.wishlistButton),
      infoSection: this.$component.querySelector(this.SELECTORS.infoSection),
      sizeGuideTrigger: this.$component.querySelector(this.SELECTORS.sizeGuideTrigger),
      swatchBullets: this.$component.querySelectorAll(this.SELECTORS.swatchBullet),
      sizeBullets: this.$component.querySelectorAll(this.SELECTORS.sizeBullet),
      swatchColor: this.$component.querySelector(this.SELECTORS.swatchColor),
      // pdpCta/chatCta interactions
      pdpCtaMobile: this.$component.querySelector(this.SELECTORS.pdpCtaMobile),
      pdpNotifyMeMobile: this.$component.querySelector(this.SELECTORS.pdpNotifyMeMobile),
    };
  }

  // Handle wishlist add //
  handleWishlist() {
    if (this.dom.wishlistButton) {

      // update wishlist button for sizes
      this.$on(this.$customEvents.PRODUCT_EVENTS.sizechanged, ({payload}) => {
        const isFull = payload.isWishlist === 'full';
        // update url
        this.dom.wishlistButton.dataset.code = payload.productCode;
        // get size wishlist status
        this.dom.wishlistButton.dataset.jsWishlist = isFull ? 'full' : 'empty';
        // update size after add/remove success
        this.dom.wishlistButton.dataset.isSize = 'true';
        // update icon class
        this.dom.wishlistButton.classList.remove(isFull ? this.CLASSES.iconWishlistEmpty : this.CLASSES.iconWishlistFull);
        this.dom.wishlistButton.classList.add(isFull ? this.CLASSES.iconWishlistFull : this.CLASSES.iconWishlistEmpty);
      });

      this.dom.wishlistButton.addEventListener("click", () => {
        const addUrl = this.dom.wishlistButton.dataset.addUrl;
        const removeUrl = this.dom.wishlistButton.dataset.removeUrl;
        const productCode = this.dom.wishlistButton.dataset.code;
        const isAdding = this.dom.wishlistButton.dataset.jsWishlist === "empty";
        const isSize = this.dom.wishlistButton.dataset.isSize === 'true';
        const url = (isAdding ? addUrl : removeUrl) + productCode;
        const label = isAdding ? this.dom.wishlistButton.dataset.labelAdded : this.dom.wishlistButton.dataset.labelRemoved;
        fetch(url, {method: 'POST'})
          .then(response => {
            return response.json();
          })
          .then(text => {
            if (isAdding) {
              this.dom.wishlistButton.dataset.jsWishlist = "full";
              this.$emit(this.$customEvents.PRODUCT_EVENTS.addedtowish, {qty: text, elem: this.dom.wishlistButton});
              this.trackingComponent.trackAction('wishlistAdd', {code: productCode});
            } else {
              this.dom.wishlistButton.dataset.jsWishlist = "empty";
              this.$emit(this.$customEvents.PRODUCT_EVENTS.removedfromwish, {qty: text.count, elem: this.dom.wishlistButton});
            }
            this.liveRegion.sayMessage(label);
            if (isSize) {
              // update size for next interaction
              this.$emit(this.$customEvents.SIZES_EVENTS.sizesupdate, {
                code: productCode,
                isWishlist: this.dom.wishlistButton.dataset.jsWishlist
              });
            }
          })
          .catch(error => this.warn(error));
      });
    }
  }

  // on desktop the left info bar is sticky to the top of the container //
  setInfoSectionSticky() {
    if (window.matchMedia('(min-width: 768px)').matches) {
      const header = document.querySelector('[data-header-menu]');
      if (header) {
        const height = header.offsetHeight;
        this.dom.infoSection.style.top = height + "px";
      } else {
        this.dom.infoSection.style.top = '0';
      }
    }
  }

  scrollInfoSection() {
    if (window.matchMedia('(min-width: 768px)').matches) {
      let previousScrollPosition = 0;
      window.addEventListener("scroll", () => {
        const scrollPosition = window.pageYOffset;
        if (scrollPosition > previousScrollPosition) {
          //scrolling down
          this.dom.infoSection.scroll({
            top: this.dom.infoSection.offsetHeight,
            behavior: "smooth",
          });
        } else {
          //scrolling up
          this.dom.infoSection.scroll({
            top: -this.dom.infoSection.offsetHeight,
            behavior: "smooth",
          });
        }
        previousScrollPosition = scrollPosition;
      });
    }
  }

  // handle size guide open //
  handleSizeGuide() {
    const ctx = this;
    if (this.dom.sizeGuideTrigger) {
      this.dom.sizeGuideTrigger.addEventListener("click", (event) => {
        event.preventDefault();
        this.dom.sizeGuideTrigger.setAttribute('disabled', '');
        fetch(this.dom.sizeGuideTrigger.href)
          .then(response => response.text())
          .then(text => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, "text/html");
            const sizeGuide = doc.querySelector('[data-ajax-chunk]');
            vex.open({
              unsafeContent: sizeGuide,
              closeClassName: 'icon close --pointer',
              className: '--editorial size-guide-layer',
              showCloseButton: true,
              afterOpen: function () {
                this.closeEl.classList.remove('vex-close');
                // Make close button accessible
                ctx.makeCloseButtonAccessible(this.closeEl, sizeGuide);
                // Trap focus within the modal for accessibility
                this.trapFocusHandler = trapFocusOnModal(this.contentEl);
                ctx.importComponents("CollapseComponent")
                  .then(({default: module}) => {
                    const component = new module(sizeGuide);
                    component.render();
                  })
                  .catch(error => {
                    console.error("Failed to load the component", error.stack);
                  });
              },
              afterClose: function () {
                // Remove trap focus event listener
                if (this.trapFocusHandler) {
                  removeTrapFocusOnModal(this.contentEl, this.trapFocusHandler);
                }
                ctx.dom.sizeGuideTrigger.removeAttribute('disabled');
              }
            });
          })
          .catch(error => console.error(error));
      });
    }
  }

  // Set focus on first interactive element for accessibility
  setFocusOnFirstInteractiveElement(container) {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  // Make close button accessible by converting to proper button element
  makeCloseButtonAccessible(closeElement, sizeGuideContent) {
    if (closeElement && closeElement.tagName !== 'BUTTON') {
      // Get translated close button label from the content
      const closeButtonText = sizeGuideContent?.dataset?.closeButton;

      // Create a proper button element
      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = closeElement.className;
      closeButton.setAttribute('aria-label', `${closeButtonText}`);
      closeButton.innerHTML = closeElement.innerHTML;

      // Copy the click event
      closeButton.addEventListener('click', () => {
        closeElement.click();
      });

      // Replace the original element
      closeElement.parentNode.replaceChild(closeButton, closeElement);
    }
  }

  // pdpCta/chatCta interactions -->
  handleAddToCartCtaWithChat() {
    if (window.matchMedia('(max-width: 767px)').matches) {
      document.addEventListener('chatButtonEnabled', () => {
        // don't reinit observer if already in place
        if (this.dom.pdpCtaMobile && !this.dom.pdpCtaMobile.classList.contains('--with-chat')) {
          // stylize pdp cta when chat available
          this.dom.pdpCtaMobile.classList.add('--with-chat');
          this.trackAddToCartCtaScroll(this.dom.pdpCtaMobile);
        }
        if(this.dom.pdpNotifyMeMobile && !this.dom.pdpNotifyMeMobile.classList.contains('--with-chat')) {
          // stylize pdp notify me when chat available
          this.dom.pdpNotifyMeMobile.classList.add('--with-chat');
          this.trackAddToCartCtaScroll(this.dom.pdpNotifyMeMobile);
        }
      });
      document.addEventListener('chatButtonDisabled', () => {
        // restore style when chat unavailable
        this.dom.pdpCtaMobile.classList.remove('--with-chat');
        this.dom.pdpNotifyMeMobile.classList.remove('--with-chat');
      });
    }
  }

  trackAddToCartCtaScroll(cta) {
    const chat = document.querySelector(this.SELECTORS.chatCta);
    // root targets chat cta area (viewportH - areaH ______ areaH => 52px + 24px + space to animate pdp cta)
    const rootTopMargin = window.innerHeight - 140;
    if (cta && chat) {
      const options = {
        root: null,
        rootMargin: `-${rootTopMargin}px 0px 0px 0px`,
        threshold: 1,
      };

      function obCallback(payload) {
        try {
          // remove class if pdp cta stops being near chat cta area
          if (payload[0].isIntersecting === false) {
            cta.classList.remove('--with-chat');
          } else {
            cta.classList.add('--with-chat');
          }
        } catch (e) {
          console.warn("Error, observer stopped");
          ob.unobserve(cta);
        }
      }

      // triggers when: near chat cta area OR away from chat cta area
      const ob = new IntersectionObserver(obCallback, options);

      ob.observe(cta);

      // stop observer when chat offline
      document.addEventListener('chatButtonDisabled', () => {
        ob.disconnect();
      });
    }
  }

  // <-- pdpCta/chatCta interactions

  // Swatches interactions
  handleSwatchHover() {
    if (this.dom.swatchBullets && this.dom.swatchColor) {
      this.dom.swatchBullets.forEach(bullet => {
        bullet.addEventListener("mouseover", () => {
          this.dom.swatchColor.innerHTML = bullet.dataset.jsSwatch;
        });
        bullet.addEventListener("mouseleave", () => {
          this.dom.swatchColor.innerHTML = this.dom.swatchColor.dataset.jsSwatchColor;
        });
      });
    }
  }

  importComponents(componentName) {
    return import(`../components/${componentName}`);
  }

  bindEvents() {
    this.handleWishlist();
    this.handleSizeGuide();
    this.handleSwatchHover();
    this.handleAddToCartCtaWithChat();
  }

  render() {
    super.render();
    this.dom = this.readDOM();
    this.setInfoSectionSticky();
    this.scrollInfoSection();
    this.bindEvents();
    this.wishlistLogic.render();
    // If algolia params are stored remove them
    removeAlgoliaAttributes();
  }
}
